/* eslint-disable max-lines */
import { api } from "jiffy-api";
import { EventBus, Events } from "jiffy-store";

import { CachedDeliveryManager } from "common/utils/delivery-date/cached-delivery-manager";
import { moneyFormat } from "common/utils/filters/money";
import {
  formatDeliveryTimeRange,
  extractDayFromDate,
  shouldShowTimeRange,
  formatTimeAMPM,
  formatDeliveryDateForDisplay,
  getEarliestDeliveryDate
} from "common/utils/format/date";
import { formatCountdownLabel } from "common/utils/format/format-countdown-label";
import { FRAUD_BUFFER_MS } from "common/hooks/useDeliveryCountdown";

import selectors from "./selectors";
import { trackProductViewed } from "./track";

const CONTAINER_ID = "js-made-product-details-delivery-dates-simple-container";

const $getActiveSlide = () => document.querySelector(selectors.global.ACTIVE_COLOR_SLIDE);
const $getCutoff = () => document.querySelectorAll(selectors.made.CUTOFF);
const $getDeliveryDates = () => document.querySelector(selectors.global.DELIVERY_DATES);
const $getDirectDeliveryDates = () =>
  document.querySelectorAll(selectors.made.DIRECT_DELIVERY_DATE);
const $getModalShipmentList = () => document.querySelector(selectors.made.MODAL_SHIPMENTS_LIST);
const $getSpinners = () => document.querySelectorAll(selectors.made.SPINNER);
const $getWrappers = () => document.querySelectorAll(selectors.made.WRAPPER);
const $getFreeShippingThreshold = () =>
  document.querySelector(selectors.made.FREE_SHIPPING_THRESHOLD);
const $getDeliveryLabel = () => document.querySelector(selectors.made.DELIVERY_TYPE_LABEL);

const addHighLight = $element => {
  $element.classList.add(selectors.global.DATE_HIGHLIGHT);
  setTimeout(() => $element.classList.remove(selectors.global.DATE_HIGHLIGHT), 500);
};

const insertDate = (dates, $dateNode) => {
  const $date = $dateNode;
  const [singleDate] = (dates.value_full || "").split(" & ");
  // Prefer time range display for Today/Tomorrow when provided by the API
  const hasTimeRange =
    shouldShowTimeRange(singleDate) && dates?.delivery_time && dates?.delivery_time_end;
  if (hasTimeRange) {
    $date.innerHTML = `${extractDayFromDate(singleDate)} ${formatDeliveryTimeRange(dates.delivery_time, dates.delivery_time_end)}`;
    const $orderBy = document.querySelector($date.dataset.deliveryOrderBySelector);
    if ($orderBy && dates?.cutoff_label) $orderBy.textContent = `Order by ${dates.cutoff_label}`;
  } else {
    $date.innerHTML = formatDeliveryDateForDisplay(singleDate);
  }
  const deliveryDate = getEarliestDeliveryDate(dates.shipments);
  if (deliveryDate) {
    $date.dataset.deliveryDate = deliveryDate;
  }
  addHighLight($date);
};

const insertDeliveryDates = ({ cart_dates: cartDates, composition_dates: compositionDates }) => {
  $getDirectDeliveryDates().forEach(insertDate.bind(this, compositionDates));
  const shipmentsForModal = cartDates.shipments || compositionDates.shipments;
  if (shipmentsForModal) {
    $getDeliveryDates()?.classList?.remove("hidden");
    $getModalShipmentList().innerHTML = shipmentsForModal
      .map(
        ({ date }) =>
          `<div class="continue-shopping-modal__delivery-dates-item">${formatDeliveryDateForDisplay(date)}</div>`
      )
      .join("");
  }
};

const updateFreeShippingThreshold = shippingCostInfo => {
  if (!shippingCostInfo) return;
  const $freeShippingElement = $getFreeShippingThreshold();
  if ($freeShippingElement)
    $freeShippingElement.textContent = `${moneyFormat(shippingCostInfo.user_free_shipping_threshold)}`;
};

const setPDPDatesLoadingAnimation = () => {
  $getDirectDeliveryDates().forEach($directDeliveryDate => {
    if ($directDeliveryDate.dataset.invert === "true") {
      $directDeliveryDate.innerHTML = `<div class="relative top-[1px] left-0 w-full h-[10px] rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>`;
    } else {
      $directDeliveryDate.innerHTML = `<div class="relative top-[1px] left-0 w-full h-4 rounded bg-gray-200 dark:bg-gray-700 w-37 animate-pulse"></div>`;
    }
  });
};

const setPDPDatesLoading = isLoading => {
  $getSpinners()?.forEach($spinner => $spinner?.classList.toggle("shown", isLoading));
  $getWrappers()?.forEach($wrapper => $wrapper?.classList.remove("hidden"));
  if (isLoading) setPDPDatesLoadingAnimation();
};

/**
 * Applies cached delivery date to the made product details component.
 * Uses the CachedDeliveryManager utility for reusable functionality.
 * @private
 */
const applyCachedDeliveryDate = () => {
  CachedDeliveryManager.applyCachedDelivery({
    deliveryDateElements: $getDirectDeliveryDates(),
    cutoffElements: $getCutoff(),
    setLoadingState: setPDPDatesLoading,
    highlightCallback: addHighLight
  });
};

// Helper function to update expedite shipping note
const updateExpediteShippingNote = deliveryData => {
  const isExpedite = (deliveryData?.value_full?.toLowerCase() || "").includes("today");
  document.querySelectorAll(".js-expedite-shipping-note").forEach($note => {
    if (!$note) return;
    $note.textContent = isExpedite ? ", with expedite shipping" : "";
    const parent = $note.parentElement;
    if (parent) {
      if (isExpedite) parent.setAttribute("data-expedite-shipping", "true");
      else parent.removeAttribute("data-expedite-shipping");
    }
  });
};

// Helper function to update delivery type label
const updateDeliveryLabel = compositionDates => {
  if (!compositionDates) return;

  const deliveryLabel =
    compositionDates.delivery_type === "local" ? "Local Delivery" : "Standard Delivery";

  const $label = $getDeliveryLabel();
  if ($label) {
    $label.textContent = deliveryLabel;
  }
};

// PO-3531: when the per-ZIP gate is on, mount <CutoffCountdown> into the
// wrapper div post-LCP so the surface flips from "Order by HH:MM" to
// "Order in the next X". The static label above stays in place during
// the requestIdleCallback gap so users never see a flash of empty.
const mountCountdownIfGated = (cutoffSource, formattedTime) => {
  const mounted = new Set();
  if (!window.jiffy?.flip?.deliveryCountdownPdp) return mounted;
  const cutoffMs = new Date(cutoffSource).getTime();
  if (!Number.isFinite(cutoffMs)) return mounted;
  const cutoffISO = new Date(cutoffMs).toISOString();
  $getCutoff().forEach($cutoff => {
    const container = $cutoff.parentNode;
    if (!container?.hasAttribute?.("data-cutoff-react-mount")) return;
    const fallbackHTML = `Order by <b class="${$cutoff.className}">${formattedTime}</b>`;
    mounted.add(container);
    import("components/jiffy/delivery-countdown/mount").then(
      ({ mountDeliveryCountdown, scheduleAfterLcp }) => {
        scheduleAfterLcp(() =>
          mountDeliveryCountdown(container, {
            cutoffAt: cutoffISO,
            variant: "pdp",
            fallbackHtml: fallbackHTML
          })
        );
      }
    );
  });
  return mounted;
};

// Match React's first paint when the live countdown applies so the static
// markup written here is byte-equal to <CutoffCountdown>'s render output —
// avoids a visible "Order by 5pm" → "Order in the next X" flip mid-load.
// Falls back to the legacy "Order by HH:MM" text otherwise.
const paintCutoffFirstPass = (cutoffSource, formattedTime) => {
  const reactMountEnabled = Boolean(window.jiffy?.flip?.deliveryCountdownPdp);
  const cutoffMs = new Date(cutoffSource).getTime();
  const countdownLabel =
    reactMountEnabled && Number.isFinite(cutoffMs)
      ? formatCountdownLabel(
          Math.max(0, Math.floor((cutoffMs - FRAUD_BUFFER_MS - Date.now()) / 1000))
        )
      : null;
  $getCutoff().forEach($cutoff => {
    const container = $cutoff.parentNode;
    const willReactMount =
      reactMountEnabled && container?.hasAttribute?.("data-cutoff-react-mount");
    if (willReactMount && countdownLabel && container) {
      container.innerHTML = `<span class="cutoff-countdown cutoff-countdown--pdp" aria-live="polite" role="status" data-testid="cutoff-countdown">Order in the next <span class="cutoff-countdown__value">${countdownLabel}</span></span>`;
    } else {
      $cutoff.textContent = formattedTime || "";
    }
  });
};

const updateHTML = deliveryDates => {
  const { cart_dates: cartDates, composition_dates: compositionDates } = deliveryDates;
  if (!cartDates?.timeleft && !compositionDates?.timeleft) return;

  // Use cart cutoff when cart has shipments, fallback to compositionDates.timeleft
  const cutoffSource =
    (cartDates?.shipments?.length > 0 && cartDates?.timeleft) || compositionDates.timeleft;
  const cutoffLabel =
    (cartDates?.shipments?.length > 0 && cartDates?.cutoff_label) || compositionDates.cutoff_label;
  const shippingCostInfo = compositionDates.shipping_cost_info;

  insertDeliveryDates(deliveryDates);

  // Update cutoff elements with fixed time format (or clear if no valid time)
  if (cutoffSource) {
    const formattedTime = cutoffLabel || formatTimeAMPM(cutoffSource, false);
    paintCutoffFirstPass(cutoffSource, formattedTime);
    mountCountdownIfGated(cutoffSource, formattedTime);
  } else {
    $getCutoff().forEach($cutoff => {
      $cutoff.textContent = "";
    });
  }

  updateDeliveryLabel(compositionDates);

  // Update expedite shipping note based on delivery data
  updateExpediteShippingNote(compositionDates);

  updateFreeShippingThreshold(shippingCostInfo);
};

const isFastEtaEnabled = () => window?.jiffy?.flip?.fastDeliveryEstimate === true;

const buildDatesFromEstimate = estimate => {
  const shipment = { date: estimate.date, date_with_day_name: estimate.date, items: 1 };
  return {
    value_full: estimate.date,
    timeleft: estimate.timeleft || null,
    cutoff_label: estimate.cutoff_label || null,
    delivery_type: null,
    delivery_time: estimate.delivery_time || null,
    delivery_time_end: estimate.delivery_time_end || null,
    shipments: [shipment],
    only_cart_shipments: [shipment],
    shipping_cost_info: null
  };
};

const fetchLegacyMadeDate = async compositionId => {
  const data = await api.print.estimatedDelivery({ composition_id: compositionId });
  return data.success ? data : {};
};

const getDeliveryDateForMade = async () => {
  const { compositionId: sliderCompositionId, supportsMade } = $getActiveSlide()?.dataset || {};
  const compositionId = sliderCompositionId || window.jiffy_made_product_props?.composition_id;
  if (!compositionId || supportsMade === "false") return {};

  if (isFastEtaEnabled()) {
    try {
      const response = await api.product.fetchETA(
        { business_line: "print" },
        { composition_hash: JSON.stringify({ [compositionId]: 1 }), source: "pdp" }
      );
      const estimate = response?.success ? response?.estimates?.[compositionId] : null;
      if (estimate?.date) {
        const dates = buildDatesFromEstimate(estimate);
        return { success: true, composition_dates: dates, cart_dates: dates };
      }
    } catch {
      // Fall through to the legacy endpoint below.
    }
  }

  return fetchLegacyMadeDate(compositionId);
};

const loadDeliveryEstimates = async (useLoader = true, shouldTrack = false) => {
  // Check for cached delivery dates first
  if (CachedDeliveryManager.shouldUseCachedDelivery(CONTAINER_ID)) {
    applyCachedDeliveryDate();
    return {};
  }
  useLoader && setPDPDatesLoading(true);
  const deliveryDates = await getDeliveryDateForMade();
  useLoader && setPDPDatesLoading(false);
  updateHTML(deliveryDates);
  // Track Product Viewed only on initial page load, not on subsequent updates
  if (shouldTrack) trackProductViewed(deliveryDates);
  return deliveryDates;
};

const initMadeDates = () => {
  // Initial load - track Product Viewed
  loadDeliveryEstimates(true, true);

  // Color selection - don't track Product Viewed
  EventBus.on(Events.PRODUCT_COLOR_SELECTED, () => {
    CachedDeliveryManager.handleRefreshDeliveryDates({
      containerId: CONTAINER_ID,
      applyCachedCallback: applyCachedDeliveryDate,
      fallbackCallback: () => loadDeliveryEstimates(false, false)
    });
  });

  // Zipcode update - don't track Product Viewed
  EventBus.on(Events.ZIPCODE_UPDATED, async () => {
    await CachedDeliveryManager.handleRefreshDeliveryDates({
      containerId: CONTAINER_ID,
      applyCachedCallback: applyCachedDeliveryDate,
      fallbackCallback: () => loadDeliveryEstimates(true, false)
    });
  });
};

export default initMadeDates;
